import {
  Badge,
  Box,
  Burger,
  Button,
  Container,
  Divider,
  Grid,
  Group,
  Image,
  Menu,
  Stack,
  Tabs,
  Text,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { Form, Link, useLocation } from '@remix-run/react'
import { useState } from 'react'
import { TbChevronRight, TbMail } from 'react-icons/tb'
import type { UserSessionInfo } from '~/authenticator.server'
import { LineButton } from './line-button'

const PROJECT_YEAR = import.meta.env.VITE_PROJECT_YEAR

export const Header = ({ userInfo }: { userInfo: UserSessionInfo | null }) => {
  const [opened, { toggle }] = useDisclosure()
  const [activeTab, setActiveTab] = useState<string | null>('signup')
  const currentLocation = useLocation()

  return (
    <Box
      h='100%'
      style={{
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Group
        justify='space-between'
        mx='lg'
        align='center'
        h='100%'
        style={{ maxWidth: '100%' }}
      >
        <Box style={{ flex: 1 }}>
          <Link to='/' style={{ display: 'flex', alignItems: 'center' }}>
            <Image
              h={{ base: '28px', sm: '40px' }}
              src={
                PROJECT_YEAR ? `/logo-${PROJECT_YEAR}.svg` : '/basic-logo.svg'
              }
              fit='contain'
              alt={`シンアド就活${PROJECT_YEAR}のロゴ画像`}
            />
          </Link>
        </Box>

        <Group>
          <Group visibleFrom='sm'>
            {userInfo?.email ? (
              <Container>
                <Grid justify='center' align='center'>
                  {!userInfo?.isEmailVerified ? (
                    <Button
                      component='a'
                      href='/mypage/verify/mail'
                      variant='outline'
                      color='error'
                      size='xs'
                      mx='sm'
                    >
                      仮登録
                    </Button>
                  ) : null}
                  <Text>{userInfo.name ?? ''}</Text>
                </Grid>
              </Container>
            ) : (
              <>
                <Link to='/signup'>
                  <Button px={0} size='md' variant='transparent'>
                    新規会員登録
                  </Button>
                </Link>
                <Divider orientation='vertical' />

                <Link
                  to={`/login?redirectTo=${encodeURIComponent(currentLocation.pathname + currentLocation.search)}`}
                >
                  <Button px={0} size='md' variant='transparent'>
                    ログイン
                  </Button>
                </Link>
              </>
            )}
          </Group>

          <Box pr={0}>
            <Menu
              opened={opened}
              onClose={toggle}
              position='bottom'
              shadow='md'
              width={375}
              closeOnClickOutside={true}
              closeOnEscape={true}
              closeOnItemClick={false}
            >
              <Menu.Target>
                <Burger opened={opened} onClick={toggle} size='sm' />
              </Menu.Target>

              <Menu.Dropdown>
                {userInfo?.email ? (
                  <Menu.Item style={{ backgroundColor: 'transparent' }}>
                    <Box>
                      <Grid justify='center' align='center' pb='sm'>
                        {!userInfo?.isEmailVerified ? (
                          <Badge
                            component='a'
                            href='/mypage/verify/mail'
                            variant='outline'
                            color='error'
                            size='md'
                            radius='sm'
                            mx='md'
                          >
                            仮登録
                          </Badge>
                        ) : null}
                        <Text size='xl' fw='bold' lh='24px' ta='center'>
                          {userInfo.name ?? ''}
                        </Text>
                      </Grid>
                      {!userInfo?.isEmailVerified ? (
                        <Button
                          component='a'
                          href='/mypage/verify/mail'
                          variant='light'
                          mt='md'
                          fullWidth
                          color='error'
                        >
                          メールを認証して本登録へ進む
                        </Button>
                      ) : null}
                      <Button
                        component='a'
                        href='/mypage/edit'
                        mt='md'
                        fullWidth
                      >
                        登録情報変更
                      </Button>
                      <Form method='POST' action='/logout'>
                        <Button type='submit' variant='light' mt='md' fullWidth>
                          ログアウト
                        </Button>
                      </Form>
                    </Box>
                  </Menu.Item>
                ) : (
                  <Menu.Item style={{ backgroundColor: 'transparent' }}>
                    <Tabs
                      value={activeTab}
                      onChange={setActiveTab}
                      color='primary'
                    >
                      <Tabs.List grow justify='space-between'>
                        <Tabs.Tab value='signup'>
                          <Text
                            fw='bold'
                            size='lg'
                            c={activeTab === 'signup' ? 'primary' : 'secondary'}
                          >
                            新規会員登録
                          </Text>
                        </Tabs.Tab>

                        <Tabs.Tab value='signin'>
                          <Text
                            fw='bold'
                            size='lg'
                            c={activeTab === 'signin' ? 'primary' : 'secondary'}
                          >
                            ログイン
                          </Text>
                        </Tabs.Tab>
                      </Tabs.List>
                      <Tabs.Panel value='signup'>
                        <Stack gap='sm'>
                          <Text fw={'bold'}>メールアドレスで登録</Text>
                          <Button
                            justify='space-between'
                            leftSection={
                              <Group px={0}>
                                <TbMail size={36} />
                                <Divider orientation='vertical' my={0} py={0} />
                              </Group>
                            }
                            rightSection={<span />}
                            size='xl'
                            component={Link}
                            to='/signup'
                            fw='bold'
                            onClick={toggle}
                          >
                            新規会員登録
                          </Button>
                          <Text fw={'bold'} pt={'sm'}>
                            LINEで登録
                          </Text>
                          <LineButton
                            label='LINEで会員登録'
                            link='/auth/line?redirectTo=/mypage/edit'
                          />
                        </Stack>
                      </Tabs.Panel>
                      <Tabs.Panel value='signin'>
                        <Stack gap='sm'>
                          <Text fw={'bold'}>メールアドレスでログイン</Text>
                          <Button
                            justify='space-between'
                            leftSection={
                              <Group px={0}>
                                <TbMail size={36} />
                                <Divider orientation='vertical' my={0} py={0} />
                              </Group>
                            }
                            rightSection={<span />}
                            size='xl'
                            component={Link}
                            to={`/login?redirectTo=${encodeURIComponent(
                              currentLocation.pathname + currentLocation.search,
                            )}`}
                            fw='bold'
                            onClick={toggle}
                          >
                            ログイン
                          </Button>
                          <Text fw={'bold'} pt={'sm'}>
                            LINEでログイン
                          </Text>
                          <LineButton
                            label='LINEでログイン'
                            link={`/auth/line?redirectTo=${encodeURIComponent(
                              currentLocation.pathname + currentLocation.search,
                            )}`}
                          />
                        </Stack>
                      </Tabs.Panel>
                    </Tabs>
                  </Menu.Item>
                )}

                <Menu.Divider mt='md' />

                <Menu.Item
                  rightSection={<TbChevronRight size={24} />}
                  closeMenuOnClick={true}
                  component={Link}
                  to='/events'
                  fw='bold'
                  c='primary'
                  fs={'lg'}
                >
                  就活イベント一覧
                </Menu.Item>
                <Menu.Item
                  rightSection={<TbChevronRight size={24} />}
                  closeMenuOnClick={true}
                  component={Link}
                  to='/events?categories=企業説明会'
                  fw='bold'
                  c='primary'
                  fs={'lg'}
                >
                  企業説明会一覧
                </Menu.Item>
                <Menu.Item
                  rightSection={<TbChevronRight size={24} />}
                  closeMenuOnClick={true}
                  component={Link}
                  to='/events?categories=インターン'
                  fw='bold'
                  c='primary'
                  fs={'lg'}
                >
                  インターン一覧
                </Menu.Item>
                <Menu.Item
                  rightSection={<TbChevronRight size={24} />}
                  closeMenuOnClick={true}
                  component={Link}
                  to='/companies'
                  fw='bold'
                  c='primary'
                  fs={'lg'}
                >
                  掲載企業一覧
                </Menu.Item>
                <Menu.Item
                  rightSection={<TbChevronRight size={24} />}
                  closeMenuOnClick={true}
                  component={Link}
                  to='https://syn-ad.com/senpai-shukatsu/'
                  fw='bold'
                  c='primary'
                  fs={'lg'}
                >
                  就活お役立ち記事
                </Menu.Item>
                <Menu.Item
                  rightSection={<TbChevronRight size={24} />}
                  closeMenuOnClick={true}
                  component={Link}
                  to='https://syn-ad.com/about'
                  fw='bold'
                  c='primary'
                  fs={'lg'}
                >
                  シンアド就活とは
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Box>
        </Group>
      </Group>
    </Box>
  )
}
